import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customerService, shipmentService, invoiceService } from '../services/firebase';
import { Customer, Shipment, Invoice } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  UserCircleIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';

const CustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!id) {
        setError('No customer ID provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const [customerData, shipmentsData, invoicesData] = await Promise.all([
          customerService.getCustomerById(id),
          shipmentService.getShipmentsByCustomer(id),
          invoiceService.getInvoicesByCustomer(id),
        ]);

        if (customerData) {
          setCustomer(customerData);
          setShipments(shipmentsData);
          setInvoices(invoicesData);
        } else {
          setError('Customer not found.');
        }
      } catch (err) {
        console.error("Error fetching customer data:", err);
        setError('Failed to fetch customer details. This might be due to a missing Firestore index.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [id]);

  const handleDeactivateCustomer = async () => {
    if (!customer || !id) return;
    
    try {
      setDeactivating(true);
      await customerService.deactivateCustomer(id);
      
      // Update local state
      setCustomer({
        ...customer,
        isActive: false,
        deactivatedAt: new Date()
      });
      
      setShowDeactivateModal(false);
      // Show success message
      alert('Customer account has been deactivated successfully. All invoices and shipments have been preserved.');
      
    } catch (error) {
      console.error('Error deactivating customer:', error);
      alert('Failed to deactivate customer account. Please try again.');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-red-500 py-12">{error}</div>;
  if (!customer) return <div className="text-center text-gray-500 py-12">Customer data not available.</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Customer Header */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              {customer.photoURL ? (
                <img className="h-24 w-24 rounded-full object-cover" src={customer.photoURL} alt={customer.name} />
              ) : (
                <UserCircleIcon className="h-24 w-24 text-gray-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900">{customer.name}</h1>
                
                {/* Account Status Badge */}
                {customer.isActive === false ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <ShieldExclamationIcon className="h-4 w-4 mr-1" />
                    Account Deactivated
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    Active Account
                  </span>
                )}
              </div>
              
              <div className="mt-2 space-y-2 text-gray-600">
                <p className="flex items-center"><EnvelopeIcon className="h-5 w-5 mr-2 text-gray-400" />{customer.email}</p>
                <p className="flex items-center"><PhoneIcon className="h-5 w-5 mr-2 text-gray-400" />{customer.phone}</p>
                {customer.address ? (
                  <p className="flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2 text-gray-400" />
                    {`${customer.address.street}, ${customer.address.city}, ${customer.address.state} ${customer.address.zipCode}`}
                  </p>
                ) : (
                  <p className="flex items-center">
                    <MapPinIcon className="h-5 w-5 mr-2 text-gray-400" />
                    <span className="text-gray-400">No address provided</span>
                  </p>
                )}
                
                {/* Deactivation Info */}
                {customer.deactivatedAt && (
                  <p className="flex items-center text-sm text-red-600">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                    Deactivated on {new Date(customer.deactivatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex flex-col space-y-2">
            {customer.isActive !== false ? (
              <button
                onClick={() => setShowDeactivateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-red-700 bg-white rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <ShieldExclamationIcon className="h-4 w-4 mr-2" />
                Deactivate Account
              </button>
            ) : (
              <button
                onClick={async () => {
                  try {
                    await customerService.reactivateCustomer(id!);
                    setCustomer({
                      ...customer,
                      isActive: true,
                      deactivatedAt: undefined
                    });
                    alert('Customer account has been reactivated successfully!');
                  } catch (error) {
                    alert('Failed to reactivate customer account. Please try again.');
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-green-300 text-green-700 bg-white rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
              >
                Reactivate Account
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Shipments and Invoices Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Shipments List */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Recent Shipments</h2>
           {shipments.length > 0 ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {shipments.map(shipment => (
                  <li key={shipment.id}>
                    <Link to={`/shipments/${shipment.id}`} className="block hover:bg-gray-50 p-4">
                      <div className="flex justify-between">
                        <p className="font-semibold text-blue-600">{shipment.trackingNumber}</p>
                        <p className={`text-sm font-medium px-2 py-1 rounded-full ${
                          shipment.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>{shipment.status}</p>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Origin: {shipment.origin?.city || 'N/A'}, Destination: {shipment.destination?.city || 'N/A'}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p>No shipments found for this customer.</p>
            </div>
          )}
        </div>

        {/* Invoices List */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Recent Invoices</h2>
           {invoices.length > 0 ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ul className="divide-y divide-gray-200">
                {invoices.map(invoice => (
                  <li key={invoice.id}>
                    <Link to={`/invoices/${invoice.id}`} className="block hover:bg-gray-50 p-4">
                       <div className="flex justify-between">
                        <p className="font-semibold text-indigo-600">{invoice.invoiceNumber}</p>
                        <p className={`text-sm font-medium px-2 py-1 rounded-full ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>{invoice.status}</p>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500 mt-1">
                        <span>Total: ${invoice.total.toFixed(2)}</span>
                        <span>Due: {new Date(invoice.dueDate).toLocaleDateString()}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p>No invoices found for this customer.</p>
            </div>
          )}
        </div>
      </div>

      {/* Deactivate Customer Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Deactivate Customer Account</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to deactivate <strong>{customer.name}</strong>'s account?
                </p>
                <div className="mt-4 text-left bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> This action will:
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                    <li>• Prevent the customer from logging in</li>
                    <li>• Hide them from the main customers list</li>
                    <li>• <strong>Preserve all invoices and shipments</strong></li>
                    <li>• <strong>Keep all business data intact</strong></li>
                  </ul>
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={handleDeactivateCustomer}
                  disabled={deactivating}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                >
                  {deactivating ? 'Deactivating...' : 'Deactivate Account'}
                </button>
                <button
                  onClick={() => setShowDeactivateModal(false)}
                  className="mt-2 px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail; 


